import { createSelector } from 'reselect'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectUserIdFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectReceiptAttachmentsListModels } from 'src/redux/selectors/data/receiptAttachments.selectors'
import {
  selectSelectedVirtualCardIdsFromState,
  selectVirtualCardListModels,
  selectVirtualCardPermissionsModelsFromState,
} from 'src/redux/selectors/data/virtualCards.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { VirtualCardsFullPaginatedDataState } from 'src/redux/virtualCardsFullPaginatedData.reducer'
import { ErrorDetailMap } from 'src/utils/error.utils'

export const selectUsersFullPaginatedDataFromState = (
  state: RootState,
): UsersFullPaginatedDataState =>
  state.app.virtualCards.virtualCardsAllCards.usersFullPaginatedData

export const selectVirtualCardsFullPaginatedData = (
  state: RootState,
): VirtualCardsFullPaginatedDataState =>
  state.app.virtualCards.virtualCardsAllCards.virtualCardsFullPaginatedData

const selectUniqueVirtualCardIds = createSelector(
  [selectVirtualCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)

const selectUniqueVirtualCardListModels = createSelector(
  [selectUniqueVirtualCardIds, selectVirtualCardListModels],
  (ids, virtualCards) => ids.map((id) => virtualCards[id]),
)

export const selectSelectedVirtualCardListModels = createSelector(
  [selectSelectedVirtualCardIdsFromState, selectVirtualCardListModels],
  (ids, virtualCards) => (ids ? ids.map((id) => virtualCards[id]) : []),
)

export const selectFilteredVirtualCards = createSelector(
  [
    selectUniqueVirtualCardListModels,
    selectUserIdFromState,
    selectUserListModelFromState,
  ],
  (virtualCards, userId, user) =>
    virtualCards.filter(
      (vc) =>
        vc.cardholderId === userId ||
        (vc.recipientId !== userId && vc.cardholderId !== userId) ||
        user?.hasOrgBookkeeperPermissions ||
        vc.recipientId === userId,
    ),
)

const selectReceiptAttachmentIdsFromState = (state: RootState): string[] =>
  state.app.virtualCards.virtualCardsAllCards.receiptAttachmentIds

export const selectReceiptAttachmentModelsList = createSelector(
  [selectReceiptAttachmentIdsFromState, selectReceiptAttachmentsListModels],
  (ids, receiptAttachments) =>
    Object.keys(receiptAttachments).length > 0
      ? ids.map((id) => receiptAttachments[id])
      : [],
)

export const selectUploadingAttachmentsFromState = (
  state: RootState,
): Record<string, string> =>
  state.app.virtualCards.virtualCardsAllCards.uploadingAttachmentThumbnails

export const selectFilteredVirtualCardsPermissions = createSelector(
  [
    selectVirtualCardPermissionsModelsFromState,
    selectSelectedVirtualCardListModels,
  ],
  (permissions, virtualCards) =>
    virtualCards.map((vc) => {
      return {
        ...permissions[vc.id],
        virtualCardId: vc.id,
      }
    }),
)

const selectUpdateableSelectedVirtualCardIds = createSelector(
  [selectFilteredVirtualCardsPermissions],
  (permissions) =>
    permissions
      .filter((permission) =>
        permission?.permissions?.includes(
          VirtualCardPermissions.UpdateVirtualCard,
        ),
      )
      .map((permission) => permission.virtualCardId),
)

export const selectUpdateableSelectedVirtualCardListModels = createSelector(
  [selectUpdateableSelectedVirtualCardIds, selectVirtualCardListModels],
  (ids, virtualCards) => (ids ? ids.map((id) => virtualCards[id]) : []),
)

export const selectUniqueCreditCardIds = createSelector(
  [selectUpdateableSelectedVirtualCardListModels],
  (data) => {
    const creditCardIds = data.map((vc) => vc.creditCardId)
    return creditCardIds.filter((id, i) => creditCardIds.indexOf(id) === i)
  },
)

export const selectFilteredCreditCardListModel = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) =>
    ids[0] && ids.length === 1 ? creditCards[ids[0]] : undefined,
)

export const selectBulkDeactivatingFromState = (state: RootState): boolean =>
  state.app.virtualCards.virtualCardsAllCards.bulkDeactivating

export const selectBulkUpdatingFromState = (state: RootState): boolean =>
  state.app.virtualCards.virtualCardsAllCards.bulkUpdating

export const selectBulkUpdateTopLevelErrorFromState = (
  state: RootState,
): string | undefined =>
  state.app.virtualCards.virtualCardsAllCards.bulkUpdateTopLevelError

export const selectBulkUpdateErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined =>
  state.app.virtualCards.virtualCardsAllCards.bulkUpdateErrorDetailMap
